import reject from 'lodash.reject'
import produce from 'immer'

import {
  useDeleteCommentMutation,
  GetActivityFeedQuery,
  CommentInfoFragment,
  GetDetailViewArtboardDocument,
  GetShareDocument,
} from 'gqlTypes'
import { ErrorHandler } from 'modules/tracing'
import { updateActivityFeed } from './updateActivityFeed'
import { useParams } from 'react-router-dom'
import { RouteParams } from 'constants/routes'

interface CommentDeleteProps {
  permanentArtboardShortId: string
  documentVersionShortId: string
  parentCommentIdentifier?: string
}

export const useCommentDelete = ({
  permanentArtboardShortId,
  documentVersionShortId,
  parentCommentIdentifier,
}: CommentDeleteProps) => {
  const { shareID } = useParams<RouteParams<'ARTBOARD_DETAIL'>>()

  const removeReplyFromComment = (
    activities: GetActivityFeedQuery['activityFeed'],
    parentCommentIndex: number,
    commentId: string
  ) => {
    // Remove deleted reply from comment replies
    return produce(
      activities?.entries[parentCommentIndex!],
      (draft: CommentInfoFragment) => {
        draft.replies = reject(
          draft?.replies,
          ({ identifier }) => identifier === commentId
        )
      }
    ) as CommentInfoFragment
  }

  return useDeleteCommentMutation({
    redirectErrors: true,
    onError: 'show-toast',
    update: (cache, { data }) => {
      const commentId = data?.deleteComment?.comment?.identifier
      if (!commentId) {
        ErrorHandler.shouldNeverHappen('Comment identifier is undefined')
        return
      }

      updateActivityFeed({
        client: cache,
        variables: { permanentArtboardShortId, shareIdentifier: shareID },
        update: activityFeed => {
          if (parentCommentIdentifier) {
            const parentCommentIndex = activityFeed?.entries.findIndex(
              activity => activity.identifier === parentCommentIdentifier
            )

            // Update comment in activities
            activityFeed.entries[parentCommentIndex!] = removeReplyFromComment(
              activityFeed,
              parentCommentIndex,
              commentId
            )
          } else {
            activityFeed.entries = reject(
              activityFeed?.entries,
              ({ identifier }) => identifier === commentId
            )
          }
        },
      })
    },
    refetchQueries: () => {
      // Refetch queries to update the subscription status for artboards
      // and document
      return [
        {
          query: GetDetailViewArtboardDocument,
          variables: {
            permanentArtboardShortId,
            documentVersionShortId,
            shareIdentifier: shareID,
          },
        },
        {
          query: GetShareDocument,
          variables: {
            shortId: shareID,
          },
        },
      ]
    },
  })
}
